import React, { useRef, useState } from 'react';
import organizationService from '../../services/organization.service';
import {
  useCreateUserRole,
  useDeleteUserRole,
  useUpdateUserRole,
} from '../../hooks/useUserQueries';
import type { UpdateUserRolePayload, UserRoleOrgBinding } from '../../hooks/useUserQueries';
import { useCompanyOptions, useDepartmentOptions, useTeamOptions } from '../../hooks/useOrgQueries';
import { ManagedUser } from '../../types/auth.types';
import { UserRole } from '../../types/organization.types';
import { LedgerDialog, LedgerField, LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';

type RoleValue = 'ADMIN' | 'MANAGER' | 'USER';
type OrgLevel = 'company' | 'team' | 'department';
type BulkAction = 'assign' | 'change' | 'remove';
type Phase = 'form' | 'running' | 'results';

interface ItemFailure {
  fullName: string;
  reason: string;
}

interface BulkBarProps {
  selected: ManagedUser[];
  onClear: () => void;
  /** Receives the run summary ("{S} succeeded · {F} failed") for the page snackbar. */
  onDone: (message: string) => void;
}

const ROLE_OPTIONS: LedgerSelectOption[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'USER', label: 'User' },
];

const LEVEL_OPTIONS: LedgerSelectOption[] = [
  { value: 'company', label: 'Company' },
  { value: 'team', label: 'Team' },
  { value: 'department', label: 'Department' },
];

const DIALOG_TITLES: Record<BulkAction, string> = {
  assign: 'Assign role',
  change: 'Change role',
  remove: 'Remove roles',
};

const ORG_PLACEHOLDERS: Record<OrgLevel, string> = {
  company: 'Select a company',
  team: 'Select a team',
  department: 'Select a department',
};

// Role updates must echo the row's existing org binding at the same level —
// the serializer re-validates the exactly-one binding on PATCH, so a
// role-only payload 400s. Rows are read most-specific-first.
const rowBinding = (row: UserRole): UserRoleOrgBinding => {
  if (row.department) return { department: row.department };
  if (row.team) return { team: row.team };
  // A company-less fall-through yields an empty binding that the mutation
  // guard rejects, surfacing as that user's per-item failure.
  return { company: row.company ?? '' };
};

const reasonOf = (error: unknown): string => {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (detail) return detail;
  const message = (error as { message?: string })?.message;
  return message || 'failed';
};

/**
 * Fixed bottom bar for multi-select bulk role operations. Each selected user
 * is one operation (possibly several sequential mutations); operations run
 * through Promise.allSettled so one user's failure never aborts the rest,
 * and any failures land in a per-item results panel.
 */
export const BulkBar: React.FC<BulkBarProps> = ({ selected, onClear, onDone }) => {
  const [action, setAction] = useState<BulkAction | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [role, setRole] = useState<RoleValue>('USER');
  const [level, setLevel] = useState<OrgLevel>('company');
  const [orgId, setOrgId] = useState('');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState('');
  const [failures, setFailures] = useState<ItemFailure[]>([]);

  const createUserRole = useCreateUserRole();
  const updateUserRole = useUpdateUserRole();
  const deleteUserRole = useDeleteUserRole();
  const roleTriggerRef = useRef<HTMLButtonElement>(null);

  // Only the assign dialog needs org options; all three fetches stay gated
  // behind it.
  const optionsEnabled = action === 'assign';
  const companies = useCompanyOptions(optionsEnabled);
  const teams = useTeamOptions(optionsEnabled);
  const departments = useDepartmentOptions(optionsEnabled);

  if (selected.length === 0) return null;

  const executing = phase === 'running';

  const openAction = (next: BulkAction) => {
    setRole('USER');
    setLevel('company');
    setOrgId('');
    setDone(0);
    setTotal(0);
    setSummary('');
    setFailures([]);
    setPhase('form');
    setAction(next);
  };

  const finishResults = () => {
    setAction(null);
    onDone(summary);
    onClear();
  };

  const runBulk = async (operation: (user: ManagedUser) => Promise<unknown>) => {
    const users = selected;
    setTotal(users.length);
    setDone(0);
    setPhase('running');
    const settled = await Promise.allSettled(
      users.map((user) => operation(user).finally(() => setDone((count) => count + 1))),
    );
    const runFailures: ItemFailure[] = [];
    settled.forEach((result, index) => {
      if (result.status === 'rejected') {
        runFailures.push({ fullName: users[index].fullName, reason: reasonOf(result.reason) });
      }
    });
    const runSummary = `${users.length - runFailures.length} succeeded · ${runFailures.length} failed`;
    if (runFailures.length === 0) {
      // Nothing per-item to read, so skip the results panel: close outright
      // and hand the summary straight to the snackbar path.
      setAction(null);
      onDone(runSummary);
      onClear();
      return;
    }
    setSummary(runSummary);
    setFailures(runFailures);
    setPhase('results');
  };

  const runAssign = () => {
    const binding: Partial<Record<OrgLevel, string>> =
      level === 'company' ? { company: orgId } : level === 'team' ? { team: orgId } : { department: orgId };
    return runBulk((user) => createUserRole.mutateAsync({ email: user.email, role, ...binding }));
  };

  const runChange = () =>
    runBulk(async (user) => {
      const rows = await organizationService.getUserRolesFor(user.id);
      if (rows.length === 0) throw new Error('No roles to change');
      for (const row of rows) {
        const data: UpdateUserRolePayload = { role, ...rowBinding(row) };
        await updateUserRole.mutateAsync({ id: row.id, data });
      }
    });

  const runRemove = () =>
    runBulk(async (user) => {
      // Zero rows fall straight through: nothing to remove is a success.
      const rows = await organizationService.getUserRolesFor(user.id);
      for (const row of rows) {
        await deleteUserRole.mutateAsync(row.id);
      }
    });

  let dialog: React.ReactNode = null;
  if (action != null) {
    const orgOptions: LedgerSelectOption[] = [
      { value: '', label: ORG_PLACEHOLDERS[level] },
      ...(level === 'company'
        ? (companies.data ?? []).map((company) => ({ value: company.id, label: company.name }))
        : level === 'team'
          ? (teams.data ?? []).map((team) => ({ value: team.id, label: `${team.name} (${team.companyName})` }))
          : (departments.data ?? []).map((department) => ({
              value: department.id,
              label: `${department.name} (${department.teamName})`,
            }))),
    ];
    const orgPending =
      level === 'company' ? companies.isPending : level === 'team' ? teams.isPending : departments.isPending;

    let body: React.ReactNode = null;
    if (phase === 'running') {
      body = (
        <p className="font-mono text-scale-1 text-ink">
          {done}/{total}…
        </p>
      );
    } else if (phase === 'results') {
      body = (
        <div>
          <p className="font-mono text-scale-1 text-ink">{summary}</p>
          {failures.length > 0 && (
            <ul className="mt-2">
              {failures.map((failure, index) => (
                <li
                  key={index}
                  className="border-t border-rule-faint py-1 text-scale-1 text-sev-critical first:border-t-0"
                >
                  {failure.fullName}: {failure.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    } else if (action === 'assign') {
      const optionsError = companies.isError || teams.isError || departments.isError;
      body = (
        <>
          {optionsError && (
            <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
              Failed to load organizations
            </p>
          )}
          <LedgerField label="Role">
            <LedgerSelect
              ariaLabel="Role"
              value={role}
              options={ROLE_OPTIONS}
              onValueChange={(value) => setRole(value as RoleValue)}
              triggerRef={roleTriggerRef}
            />
          </LedgerField>
          <LedgerField label="Level">
            <LedgerSelect
              ariaLabel="Level"
              value={level}
              options={LEVEL_OPTIONS}
              onValueChange={(value) => {
                setLevel(value as OrgLevel);
                setOrgId('');
              }}
            />
          </LedgerField>
          <LedgerField label="Organization">
            <LedgerSelect
              ariaLabel="Organization"
              value={orgId}
              options={orgOptions}
              onValueChange={setOrgId}
              disabled={orgPending}
            />
          </LedgerField>
        </>
      );
    } else if (action === 'change') {
      body = (
        <LedgerField label="Role">
          <LedgerSelect
            ariaLabel="Role"
            value={role}
            options={ROLE_OPTIONS}
            onValueChange={(value) => setRole(value as RoleValue)}
          />
        </LedgerField>
      );
    }
    // remove keeps an empty body: the dialog description carries the message.

    const runAction = action === 'assign' ? runAssign : action === 'change' ? runChange : runRemove;
    const confirmLabel = action === 'assign' ? 'Assign' : action === 'change' ? 'Change' : 'Remove';
    const confirmDisabled = executing || (action === 'assign' && (orgId === '' || orgPending));

    const footer =
      phase === 'results' ? (
        <button
          type="button"
          onClick={finishResults}
          className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
        >
          Close
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setAction(null)}
            disabled={executing}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={runAction}
            disabled={confirmDisabled}
            className={cn(
              'cursor-pointer px-3 py-1.5 text-scale-1 font-semibold text-paper disabled:cursor-default disabled:opacity-60',
              action === 'remove' ? 'bg-sev-critical hover:opacity-90' : 'bg-accent hover:bg-accent-hover',
            )}
          >
            {confirmLabel}
          </button>
        </>
      );

    dialog = (
      <LedgerDialog
        open
        onOpenChange={(next) => {
          if (next) return;
          // Dismissing the results panel by any path still reports the run —
          // the mutations already happened, so the selection is stale either way.
          if (phase === 'results') {
            finishResults();
          } else {
            setAction(null);
          }
        }}
        title={DIALOG_TITLES[action]}
        size={action === 'assign' ? 'md' : 'sm'}
        // Points at the assign form's Role select; when that trigger isn't
        // rendered (change/remove, running/results) the null ref falls back
        // to the dialog default.
        initialFocus={roleTriggerRef}
        description={
          action === 'remove' && phase === 'form'
            ? 'This removes every role assignment from the selected users. The users themselves remain.'
            : undefined
        }
        preventClose={executing}
        footer={footer}
      >
        {body}
      </LedgerDialog>
    );
  }

  return (
    <>
      <div
        data-print-hide
        className="fixed inset-x-0 bottom-0 z-30 flex flex-wrap items-baseline justify-between gap-y-2 border-t border-ink bg-paper-raised px-5 py-3"
      >
        <span className="font-mono text-scale-1 text-ink">{selected.length} selected</span>
        <span className="flex flex-wrap items-baseline gap-4">
          <button
            type="button"
            onClick={() => openAction('assign')}
            className="cap-label cursor-pointer !text-accent hover:underline"
          >
            Assign role
          </button>
          <button
            type="button"
            onClick={() => openAction('change')}
            className="cap-label cursor-pointer !text-accent hover:underline"
          >
            Change role
          </button>
          <button
            type="button"
            onClick={() => openAction('remove')}
            className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical"
          >
            Remove roles
          </button>
          <button
            type="button"
            onClick={onClear}
            className="cap-label cursor-pointer text-ink-muted hover:!text-ink"
          >
            Clear
          </button>
        </span>
      </div>
      {dialog}
    </>
  );
};
